<template>
  <div class="media-library-view">
    <section class="toolbar-card">
      <div>
        <h2>媒体库 Media Library</h2>
        <p>上传和管理后台可复用的图片资源。</p>
      </div>
      <div class="toolbar-card__actions">
        <el-input
          v-model.trim="filters.keyword"
          clearable
          placeholder="搜索名称/地址 Search name/url"
          size="small"
          @keyup.enter.native="handleFilterChange"
          @clear="handleFilterChange"
        />
        <el-select v-model="filters.module" clearable placeholder="模块 Module" size="small" @change="handleFilterChange" @clear="handleFilterChange">
          <el-option
            v-for="item in moduleOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-button size="small" @click="handleFilterChange">查询 Search</el-button>
        <el-upload
          action=""
          :http-request="handleUpload"
          :show-file-list="false"
          accept="image/*"
        >
          <el-button type="primary" size="small" :loading="uploading">上传图片 Upload Image</el-button>
        </el-upload>
      </div>
    </section>

    <section class="media-grid" v-loading="loading">
      <article v-for="item in assets" :key="item.id" class="media-card">
        <el-image :src="assetUrl(item.fileUrl)" :preview-src-list="[assetUrl(item.fileUrl)]" :alt="item.name" fit="cover" />
        <div class="media-card__body">
          <strong>{{ item.name }}</strong>
          <em>{{ item.module || '未分组 Uncategorized' }}</em>
          <span>{{ item.fileUrl }}</span>
          <div class="media-card__actions">
            <el-button type="text" @click="copyUrl(item.fileUrl)">复制地址 Copy URL</el-button>
            <el-button type="text" @click="openEditDialog(item)">编辑 Edit</el-button>
            <el-button type="text" class="danger-btn" @click="handleDelete(item)">删除 Delete</el-button>
          </div>
        </div>
      </article>

      <el-empty v-if="!loading && assets.length === 0" description="暂无图片 No images yet" />
    </section>

    <div class="pagination-bar">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :current-page="pagination.page"
        :page-size="pagination.pageSize"
        :page-sizes="[10, 20, 50]"
        :total="pagination.total"
        @current-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <el-dialog title="编辑媒体 Edit Media" :visible.sync="dialogVisible" width="520px" @closed="resetForm">
      <el-form ref="form" :model="form" :rules="rules" label-position="top">
        <el-form-item label="图片预览 Preview">
          <el-image v-if="form.fileUrl" class="edit-preview" :src="assetUrl(form.fileUrl)" :preview-src-list="[assetUrl(form.fileUrl)]" :alt="form.name" fit="cover" />
        </el-form-item>
        <el-form-item label="名称 Name" prop="name">
          <el-input v-model.trim="form.name" />
        </el-form-item>
        <el-form-item label="模块 Module">
          <el-select v-model="form.module" clearable placeholder="请选择 Please select">
            <el-option
              v-for="item in moduleOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="图片地址 Image URL">
          <el-input :value="form.fileUrl" readonly />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消 Cancel</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">保存 Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { deleteMediaAsset, fetchMediaAssets, resolveAssetUrl, updateMediaAsset, uploadMediaAsset } from '../../api/media'

const createEmptyForm = () => ({
  id: null,
  name: '',
  module: '',
  fileUrl: ''
})

export default {
  name: 'MediaLibraryView',
  data () {
    return {
      assets: [],
      filters: {
        keyword: '',
        module: ''
      },
      moduleOptions: [
        { label: '产品 Product', value: 'product' },
        { label: '分类 Category', value: 'category' },
        { label: '案例 Case', value: 'case' },
        { label: '文章 Article', value: 'article' },
        { label: '页面区块 Page Section', value: 'page-section' },
        { label: '媒体库 Media', value: 'media' }
      ],
      loading: false,
      uploading: false,
      submitting: false,
      dialogVisible: false,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      form: createEmptyForm(),
      rules: {
        name: [{ required: true, message: '请输入名称 Please input name.', trigger: 'blur' }]
      }
    }
  },
  async created () {
    await this.loadData()
  },
  methods: {
    assetUrl: resolveAssetUrl,
    mediaQueryParams () {
      return {
        page: this.pagination.page,
        pageSize: this.pagination.pageSize,
        keyword: this.filters.keyword || undefined,
        module: this.filters.module || undefined
      }
    },
    async loadData () {
      this.loading = true
      try {
        const res = await fetchMediaAssets(this.mediaQueryParams())
        const data = res.data
        this.assets = Array.isArray(data) ? data : data.items
        this.pagination.total = Array.isArray(data) ? data.length : data.total
      } catch (error) {
        this.$message.error('媒体库加载失败 Failed to load media.')
      } finally {
        this.loading = false
      }
    },
    handleFilterChange () {
      this.pagination.page = 1
      this.loadData()
    },
    handlePageChange (page) {
      this.pagination.page = page
      this.loadData()
    },
    handlePageSizeChange (pageSize) {
      this.pagination.page = 1
      this.pagination.pageSize = pageSize
      this.loadData()
    },
    async handleUpload ({ file }) {
      if (this.uploading) return
      this.uploading = true
      try {
        await uploadMediaAsset(file, 'media')
        this.$message.success('图片已上传 Image uploaded.')
        await this.loadData()
      } catch (error) {
        this.$message.error((error.response && error.response.data && error.response.data.message) || '上传失败 Upload failed.')
      } finally {
        this.uploading = false
      }
    },
    openEditDialog (item) {
      this.form = {
        id: item.id,
        name: item.name || '',
        module: item.module || '',
        fileUrl: item.fileUrl || ''
      }
      this.dialogVisible = true
    },
    resetForm () {
      this.form = createEmptyForm()
      if (this.$refs.form) this.$refs.form.resetFields()
    },
    handleSubmit () {
      this.$refs.form.validate(async valid => {
        if (!valid || this.submitting) return
        this.submitting = true
        try {
          await updateMediaAsset(this.form.id, {
            name: this.form.name,
            module: this.form.module || null
          })
          this.$message.success('媒体已更新 Media updated.')
          this.dialogVisible = false
          await this.loadData()
        } catch (error) {
          this.$message.error((error.response && error.response.data && error.response.data.message) || '保存失败 Save failed.')
        } finally {
          this.submitting = false
        }
      })
    },
    copyUrl (fileUrl) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(fileUrl)
      }
      this.$message.success('图片地址已复制 URL copied.')
    },
    handleDelete (item) {
      this.$confirm(`确认删除图片“${item.name}”吗？\nConfirm deleting this image?`, '删除确认 Delete Confirmation', {
        type: 'warning'
      }).then(async () => {
        try {
          await deleteMediaAsset(item.id)
          this.$message.success('图片已删除 Image deleted.')
          await this.loadData()
        } catch (error) {
          if (error.response && error.response.status === 409) {
            this.showReferenceWarning(error.response.data.references || [])
            return
          }
          this.$message.error((error.response && error.response.data && error.response.data.message) || '删除失败 Delete failed.')
        }
      }).catch(() => {})
    },
    showReferenceWarning (references) {
      const content = references.length
        ? references.map(item => `${item.module} / ${item.title} / ${item.field}`).join('\n')
        : '该图片仍被内容引用 This image is still referenced.'

      this.$alert(content, '图片正在使用 Image In Use', {
        confirmButtonText: '知道了 OK',
        customClass: 'reference-alert'
      })
    }
  }
}
</script>

<style scoped>
.toolbar-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 24px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
  box-shadow: 0 16px 32px rgba(23, 20, 17, 0.04);
}

.toolbar-card h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 500;
}

.toolbar-card p {
  margin: 8px 0 0;
  color: var(--muted);
}

.toolbar-card__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.toolbar-card__actions .el-input,
.toolbar-card__actions .el-select {
  width: 210px;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  min-height: 280px;
  margin-top: 18px;
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  padding: 14px 4px 2px;
}

.media-card {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
}

.media-card .el-image {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  background: #f2eee8;
}

.media-card__body {
  display: grid;
  gap: 8px;
  padding: 12px;
}

.media-card__body strong,
.media-card__body em,
.media-card__body span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.media-card__body em {
  color: #8a7158;
  font-size: 12px;
  font-style: normal;
}

.media-card__body span {
  color: var(--muted);
  font-size: 12px;
}

.media-card__actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.edit-preview {
  display: block;
  width: 100%;
  max-width: 260px;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #f2eee8;
}

.danger-btn {
  color: #d14343;
}

@media (max-width: 720px) {
  .toolbar-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .toolbar-card__actions,
  .toolbar-card__actions .el-input,
  .toolbar-card__actions .el-select {
    width: 100%;
  }
}
</style>
